import { ReactNode, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "@mui/base/Modal";
import { IconClose } from "../../icons/IconClose/IconClose";
import commonContent from "../../../content/common";
import "./LeafModal.scss";
import { Button } from "../../components/Button/Button";
import { useFullscreenPortalTarget } from "../../util/fullscreenPortalTarget";

type Props = {
  open: boolean;
  onCloseCallbackFn?: Function;
  title?: string;
  subtitle?: string | JSX.Element;
  description?: string | JSX.Element;
  children?: ReactNode;
  large?: boolean;
  id: string;
  backdropStyle?: "gray" | "frosted";
};

export const LeafModal = ({
  open,
  onCloseCallbackFn = () => {},
  title,
  subtitle,
  description,
  children,
  id,
  large = false,
  backdropStyle = "gray",
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalOpenedByElement, setModalOpenedByElement] = useState<Element>();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Keeps the modal visible while a TreeView-style fullscreen element is active —
  // otherwise the default document.body portal target renders outside the
  // fullscreened subtree, which the browser simply doesn't paint.
  const portalTarget = useFullscreenPortalTarget();

  useEffect(() => {
    if (open) {
      setIsOpen(open);
      // setTimeout is used because, in some instances, closeButtonRef was not initialized when the modal was opened.
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 0);
    }
  }, [open]);

  const { t } = useTranslation(["common"]);

  if (!isOpen) return null;

  const handleOnClose = () => {
    setIsOpen(false);
    onCloseCallbackFn(false);
    if (modalOpenedByElement) {
      (modalOpenedByElement as HTMLElement).focus();
      setModalOpenedByElement(undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOnClose();
    }
  };

  const returnSubtitle = () => {
    if (subtitle) {
      return <p className="leaf-modal__modal-subtitle">{subtitle}</p>;
    }
    return <></>;
  };

  const modalBackdrop = (
    <div
      onClick={handleOnClose}
      role="presentation"
      className={`${id} leaf-modal__backdrop ${backdropStyle === "frosted" ? "leaf-modal__backdrop--frosted" : ""}`.trim()}
      data-testid={`leafModal-overlay`}
    ></div>
  );

  return open ? (
    <div>
      {ReactDOM.createPortal(modalBackdrop, portalTarget)}
      <Modal
        open={open}
        aria-hidden={!open}
        onClose={handleOnClose}
        className={`${id}`}
        aria-modal={true}
        role="dialog"
        aria-label={title}
        container={portalTarget}
      >
        <div className={`leaf-modal__modal-container ${large ? "leaf-modal__modal-container--large" : ""}`}>
          <div className="leaf-modal__close-icon">
            <Button
              className="leaf-modal__close-icon__close"
              aria-label={`${t("closeButton", commonContent["closeButton"])}`}
              onClick={handleOnClose}
              id="leafModalCloseButton"
              onKeyDown={handleKeyDown}
              ref={closeButtonRef}
            >
              <IconClose width="1.4rem" height="1.4rem" />
            </Button>
          </div>
          <div className="leaf-modal__modalHeader">
            <h1 className="leaf-modal__title">{title}</h1>
            {returnSubtitle()}
          </div>
          <div className="leaf-modal__modal-content">
            <div className="leaf-modal__modal-body">
              {description && <div className="leaf-modal__modal-desc">{description}</div>}
              {children}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  ) : (
    <></>
  );
};
